import {
  Typography,
  Box,
  Grid,
  useMediaQuery,
  useTheme,
  Button,
  Tooltip
} from '@mui/material';
import DialogWithCloseIcon from '@/components/ui/dialogs/dialog-with-close-icon';
import {parseISO, differenceInYears} from 'date-fns';
import ParticipantAvatarBig from '@/components/participant/participant-avatar-big';
import {useTranslation} from 'react-i18next';
import {Sport} from '@/types/common';
import {Variant} from '@mui/material/styles/createTypography';
import SportsComponent from './sports';
import {blockUser, unBlockUser, useTitleStyle} from '@/utils/';
import Linkify from 'linkify-react';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {useMatrixRoomContext} from '@/contexts/matrix/room';
import ChatWindow from '@/components/matrix-chat/ui/chat-window';
import {useMatrixContext} from '@/contexts/matrix/client';
import {useAppSelector} from '@/redux-store/store';
import {
  getUserEmail,
  makeMatrixUserIdFromUserMail
} from '@/components/matrix-chat/utils/helper-functions';
import {getMatrixUserName} from '@/services/matrix/utils';
import ConfirmationDialog from '@/components/ui/dialogs/confirmation-dialog';
import BlockIcon from '@mui/icons-material/Block';

export type UserInOverview = {
  _id: string;
  name: string | undefined;
  birthday: string;
  gender: string;
  color: string;
  updatedAt: string;
  description?: string;
  hometown?: string;
  sports?: Array<{level: number; sport: Sport}>;
  picture?: string;
};

interface UserOverviewProps {
  userData: UserInOverview | null;
  onClose: () => void;
  userViewDialogOpen: boolean;
}

const UserOverview: React.FC<UserOverviewProps> = ({
  userData,
  onClose,
  userViewDialogOpen
}) => {
  const theme = useTheme();
  const isSmScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const authUserData = useAppSelector((state) => state.auth);
  const [matrixUserName, setMatrixUserName] = useState<string | null>(null);
  const [blockConfirmationOpen, setBlockConfirmationOpen] = useState(false);
  const [unblockConfirmationOpen, setUnblockConfirmationOpen] = useState(false);
  const [userMail, setUserMail] = useState<string | null>(null);
  const [isCreatingChat, setIsCreatingChat] = useState(false);

  const {initMatrixRoom} = useMatrixRoomContext();
  const {matrixClient, matrixUserId} = useMatrixContext();

  const {handleCloseWindowChat, windowChatOpen} = useMatrixRoomContext();

  const {t} = useTranslation();

  const isUserBlacklisted = useMemo(() => {
    if (userData?._id) {
      return authUserData.userBlacklist.includes(userData._id);
    }
  }, [authUserData.userBlacklist, userData]);

  const inviteUserUserId = useMemo(() => {
    return userMail ? makeMatrixUserIdFromUserMail(userMail) : null;
  }, [userMail]);

  const isSelf = useMemo(() => {
    if (inviteUserUserId !== matrixUserId) {
      return false;
    }
    return true;
  }, [inviteUserUserId, matrixUserId]);

  const handleStartWindowChat = useCallback(() => {
    if (matrixClient && matrixUserId && inviteUserUserId) {
      setIsCreatingChat(true);
      try {
        if (!isSelf) {
          initMatrixRoom(inviteUserUserId);
        }
      } catch (error) {
        console.error('error: ', error);
      } finally {
        setIsCreatingChat(false);
      }
    }
  }, [initMatrixRoom, inviteUserUserId, isSelf, matrixClient, matrixUserId]);

  let age = '';
  if (userData) {
    age = differenceInYears(new Date(), parseISO(userData.birthday)).toString();
  }

  const genderAndAgeVariant = useCallback(() => {
    let gender = null;
    if (userData) {
      if (userData.gender && userData.gender !== 'NA') {
        gender = userData.gender;
      }
      if (gender && age && !isNaN(Number(age))) {
        return `${t(gender)}, ${age}`;
      } else if (gender) {
        return `${t(gender)}`;
      } else if (age && !isNaN(Number(age))) {
        return `${t(age)}`;
      }
    }
  }, [age, t, userData]);

  const handleClickBlockButton = useCallback(() => {
    setBlockConfirmationOpen(true);
  }, []);

  const handleCancelBlockRequest = useCallback(() => {
    setBlockConfirmationOpen(false);
  }, []);

  const handleClickUnblockButton = useCallback(() => {
    setUnblockConfirmationOpen(true);
  }, []);

  const handleCancelUnblockRequest = useCallback(() => {
    setUnblockConfirmationOpen(false);
  }, []);

  const handleBlockUser = useCallback(async () => {
    const id = userData?._id;
    if (id && matrixClient) {
      await blockUser(id, matrixClient);
      setBlockConfirmationOpen(false);
    }
  }, [matrixClient, userData?._id]);

  const handleUnblockUser = useCallback(async () => {
    const id = userData?._id;
    if (id && matrixClient) {
      await unBlockUser(id, matrixClient);
      setUnblockConfirmationOpen(false);
    }
  }, [matrixClient, userData?._id]);

  const nameFontSize = isSmScreen ? 'h6' : 'h4';

  const descFontSize = isSmScreen ? 'body2' : 'body1';

  const nameStyle = useTitleStyle('26ch', undefined, 'md');

  useEffect(() => {
    const fetchUserEmail = async () => {
      if (userData?._id) {
        try {
          const employeeMail = await getUserEmail(userData._id);
          setUserMail(employeeMail);
        } catch (error) {
          console.error('Error fetching user email:', error);
          setUserMail(null);
        }
      }
    };

    fetchUserEmail();
  }, [userData]);

  useEffect(() => {
    const fetchMatrixUserName = async () => {
      if (inviteUserUserId && matrixClient) {
        const userName = await getMatrixUserName(
          matrixClient,
          inviteUserUserId
        );
        setMatrixUserName(userName);
      }
    };

    fetchMatrixUserName();
  }, [inviteUserUserId, matrixClient]);

  return userData ? (
    <>
      <DialogWithCloseIcon
        open={userViewDialogOpen}
        onClose={onClose}
        fullWidth={true}
        maxWidth="md"
      >
        <Box sx={{p: {xs: 0, sm: 3}}}>
          <Grid container direction="column" alignItems="center" spacing={2}>
            <Grid item>
              <ParticipantAvatarBig
                participant={{
                  name: userData.name,
                  color: userData.color,
                  picture: userData.picture,
                  _id: userData._id,
                  updatedAt: userData.updatedAt
                }}
                index={0}
              />
            </Grid>
            <Grid item xs={12} style={{alignItems: 'flex-start'}}>
              <Grid container spacing={3} direction="column">
                <Grid item>
                  <Box display="flex" alignItems="center">
                    <Typography
                      variant={nameFontSize as Variant}
                      component="div"
                      marginBottom={1}
                      sx={nameStyle}
                    >
                      {userData.name}
                    </Typography>
                    {isUserBlacklisted && (
                      <Tooltip title={t('user_blocked')}>
                        <BlockIcon
                          sx={{
                            cursor: 'pointer',
                            marginLeft: 2,
                            marginBottom: 0.5,
                            color: theme.palette.warning.main
                          }}
                        />
                      </Tooltip>
                    )}
                  </Box>
                  <Typography variant="body1" marginBottom={1}>
                    {userData.hometown}
                  </Typography>
                  <Typography
                    variant={isSmScreen ? 'body1' : 'h6'}
                    component="div"
                    marginBottom={1}
                  >
                    {genderAndAgeVariant()}
                  </Typography>

                  <Linkify
                    options={{
                      target: '_blank',
                      rel: 'noopener noreferrer',
                      attributes: {
                        style: {
                          color: theme.palette.customColors?.urlLink
                        }
                      }
                    }}
                  >
                    <Typography
                      variant={descFontSize as Variant}
                      marginBottom={1}
                    >
                      {userData.description}
                    </Typography>
                  </Linkify>
                </Grid>
                {userData.sports && userData.sports.length > 0 && (
                  <SportsComponent userData={userData} />
                )}
              </Grid>
            </Grid>
          </Grid>
          <Grid marginTop={4}>
            {!isSelf && (
              <Box width="100%">
                <Grid container spacing={2} justifyContent="center">
                  <Grid container sm={6} xs={12} justifyContent="center">
                    <Button
                      variant="contained"
                      color="secondary"
                      onClick={
                        isUserBlacklisted
                          ? handleClickUnblockButton
                          : handleClickBlockButton
                      }
                      sx={{
                        padding: 2
                      }}
                    >
                      {isUserBlacklisted ? t('unblock_user') : t('block_user')}
                    </Button>
                  </Grid>
                  <Grid container sm={6} xs={12} justifyContent="center">
                    {!isUserBlacklisted && (
                      <Button
                        variant="contained"
                        color="secondary"
                        onClick={handleStartWindowChat}
                        disabled={isCreatingChat}
                        sx={{
                          padding: 2
                        }}
                      >
                        {t('direct_chat')}
                      </Button>
                    )}
                  </Grid>
                </Grid>
              </Box>
            )}
          </Grid>
        </Box>
      </DialogWithCloseIcon>
      <ChatWindow
        open={windowChatOpen}
        onClose={handleCloseWindowChat}
        userName={matrixUserName}
      />
      <ConfirmationDialog
        open={blockConfirmationOpen}
        title={t('block_user')}
        message={t('block_user_description')}
        onConfirm={handleBlockUser}
        onCancel={handleCancelBlockRequest}
        warn
      />
      <ConfirmationDialog
        open={unblockConfirmationOpen}
        title={t('block_user_revert')}
        message={t('block_user_revert_description')}
        onConfirm={handleUnblockUser}
        onCancel={handleCancelUnblockRequest}
      />
      ;
    </>
  ) : null;
};

export default UserOverview;
